import { Controller } from "@hotwired/stimulus";

// Le formulaire de tentative sur un bloc de quiz (carte #1593, lot K) — enrichi
// pour l'écran 17a (*États transverses*, carte du balayage du 2026-09-07).
//
// 🔴 **`client_attempt_id` était déjà fabriqué CÔTÉ VUE, avant tout réseau** — voir
// `_block.html.erb` : « une coupure de réseau compterait deux fois » sinon. Et
// `Go4learn::Attempts::Recorder` est déjà **idempotent** par cet identifiant : le
// backend attendait un client qui rejoue, ce contrôleur est ce client.
//
// 🔴 **DEUX pannes, DEUX réponses — et ce n'est pas un détail.**
//
//  - Le RÉSEAU est coupé (le `fetch` LÈVE) : c'est l'état « hors ligne » du reste du
//    produit — silencieux, on retente tout seul au retour du réseau. Aucun écran ne
//    lui est dédié ici : c'est du ressort de 17c (réseau coupé), délibérément hors
//    scope de cette carte.
//  - Le SERVEUR répond, mais en ÉCHEC (`response.ok` faux) : la connexion a
//    fonctionné, c'est nos serveurs qui ont failli. C'est CE cas, et lui seul, que
//    17a dramatise — avec le disculpage explicite de la maquette : « pas ta
//    connexion, pas ton téléphone ».
//
// ⚠️ **Confondre les deux accuserait l'élève à la place du serveur** — un
// `catch` et un `else { !response.ok }` renvoient tous deux « ça n'a pas marché »,
// mais un seul des deux prouve que le réseau a porté la requête jusqu'au bout.
export default class extends Controller {
  static targets = ["form", "submitBtn", "i18n"];
  static values = { storageKey: String, flashcardsUrl: String, verdictUrl: String };

  connect() {
    this.t = JSON.parse(this.i18nTarget.textContent);
    // Un tampon laissé par un envoi précédent (rechargement pendant une panne
    // serveur, ou après une coupure réseau) : on retente tout de suite, sans
    // attendre un second clic.
    if (this.buffer()) this.send(this.buffer());
    this.onOnline = () => {
      const pending = this.buffer();
      if (pending) this.send(pending);
    };
    window.addEventListener("online", this.onOnline);
  }

  disconnect() {
    window.removeEventListener("online", this.onOnline);
    this.hideFailureOverlay();
  }

  // 🔴 **LE LECTEUR PAS À PAS** — arbitrage du porteur, 2026-09-12 : le quiz
  // corrige après CHAQUE réponse, comme `quizLive` du prototype.
  //
  // ⚠️ **Le verdict vient du SERVEUR.** Le verdict local aurait demandé
  // `correct` dans le DOM, ce qu'une garde du dépôt interdit sur tout le corps
  // de la page : « un `correct` dans le formulaire donnerait la réponse à qui
  // lit la source ». Une requête par réponse, et la bonne réponse ne sort
  // jamais.
  //
  // ⚠️ **Écoute DÉLÉGUÉE sur le formulaire** : `change` remonte depuis les
  // `input`, donc une seule écoute au lieu d'une par option.
  //
  // ⚠️ **Hors ligne, on le DIT plutôt que de se taire.** Un enfant qui répond
  // sans réseau verrait les autres questions se corriger et pas la sienne : la
  // phrase lui apprend que sa réponse est gardée. Le résultat final, lui,
  // reste porté par le tampon et le rejeu.
  async verdict(event) {
    const input = event.target;
    // ⚠️ **Le verdict pas à pas ne vaut QUE pour le choix unique**, et le motif
    // `answers[N]` est ce qui le borne. Les sept autres types postent
    // `responses[N][…]` : leur barème demande la réponse ENTIÈRE (l'ordre complet,
    // toutes les paires, tous les trous), et peindre « Faux » sur une remise en
    // ordre à moitié faite accuserait l'apprenant d'une erreur qu'il n'a pas
    // encore commise. Leur verdict arrive à la validation, avec l'explication.
    const match = input.name?.match(/^answers\[(\d+)\]$/);
    if (!match || !this.verdictUrlValue) return;

    const slot = this.verdictSlot(input);
    try {
      const response = await fetch(this.verdictUrlValue, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ question: match[1], answer: input.value }),
      });
      if (!response.ok) return this.paint(slot, this.t.verdict_offline, null);

      const { data } = await response.json();
      this.paint(slot,
                 data.correct ? this.t.verdict_right : this.t.verdict_wrong,
                 data.correct, data.explanation);
    } catch {
      // ⚠️ Un réseau coupé n'est PAS une mauvaise réponse : on ne peint aucun
      // verdict, on dit que la réponse est gardée.
      this.paint(slot, this.t.verdict_offline, null);
    }
  }

  // ⚠️ **L'élément est CRÉÉ À LA DEMANDE, pas rendu par la vue** : le gabarit
  // paierait un bloc vide par question sur chaque affichage, alors qu'un
  // apprenant sans JS n'en verra jamais un seul.
  verdictSlot(input) {
    const question = input.closest("[data-testid='quiz-question']");
    let slot = question.querySelector("[data-testid='quiz-step-verdict']");
    if (!slot) {
      slot = document.createElement("p");
      slot.className = "g4l-note";
      slot.dataset.testid = "quiz-step-verdict";
      question.appendChild(slot);
    }
    return slot;
  }

  // ⚠️ **La couleur vient des JETONS**, jamais d'un hexadécimal : en thème
  // sombre un vert en dur resterait clair sur fond noir (`DS-18`).
  paint(slot, label, correct, explanation) {
    slot.textContent = explanation ? `${label} — ${explanation}` : label;
    slot.style.color = correct === null
      ? "var(--g4l-fg-3)"
      : `var(--g4l-${correct ? "success" : "warn"}-ink)`;
  }

  submit(event) {
    event.preventDefault();
    const payload = this.collectAnswers();
    this.saveBuffer(payload);
    this.send(payload);
  }

  // Single choice posts `answers[N]`; the seven other kinds post `responses[N][…]`
  // (`[selected][]`, `[value]`, `[text]`, `[order][]`, `[pairs][k]`, `[blanks][k]`).
  // Both are kept, in the shapes the server reads (`ResourcesController#answers_param`):
  // keeping `answers` alone dropped every answer of those seven kinds.
  collectAnswers() {
    const answers = {};
    const responses = {};
    new FormData(this.formTarget).forEach((value, key) => {
      const single = key.match(/^answers\[(\d+)\]$/);
      if (single) {
        answers[single[1]] = value;
        return;
      }
      const typed = key.match(/^responses\[(\d+)\]\[(\w+)\](?:\[(\w*)\])?$/);
      if (!typed) return;
      const [, question, field, sub] = typed;
      const slot = (responses[question] ||= {});
      if (sub === undefined) slot[field] = value;
      else if (sub === "") (slot[field] ||= []).push(value);
      else (slot[field] ||= {})[sub] = value;
    });
    return { answers, responses };
  }

  async send(payload) {
    this.setSending(true);
    let outcome;
    let redirectUrl = null;
    // A buffer written before both kinds were kept holds the bare `answers` map.
    const { answers, responses = {} } = payload.answers ? payload : { answers: payload };
    // A buffer replayed after a reload goes to the attempt that wrote it: the page renders a
    // new `client_attempt_id`, the buffer keeps the original address and opening instant.
    const action = payload.action || this.formTarget.action;
    const startedAt = payload.started_at || this.declaredStart();
    try {
      const response = await fetch(action, {
        method: "POST",
        headers: this.headers(),
        body: JSON.stringify({ answers, responses, started_at: startedAt }),
      });
      // A refusal (attempts spent, timed quiz to start first) is FINAL, not a failure to
      // retry: the server names where to go, and the flash there says why.
      const final = response.ok || (response.status >= 400 && response.status < 500);
      const body = final ? await response.json().catch(() => null) : null;
      if (body?.data?.redirect_url) {
        outcome = "ok";
        redirectUrl = body.data.redirect_url;
      } else {
        // 🔴 Le réseau a PORTÉ la requête jusqu'à un serveur qui a répondu — c'est
        // la preuve, pas une supposition, que la connexion n'est pas en cause.
        outcome = "server_error";
      }
    } catch {
      outcome = "offline";
    }
    this.setSending(false);

    if (outcome === "ok") {
      this.clearBuffer();
      this.hideFailureOverlay();
      window.Turbo ? window.Turbo.visit(redirectUrl) : window.location.assign(redirectUrl);
      return;
    }
    // Le tampon RESTE dans les deux autres cas : c'est lui qui tient la promesse
    // « tu ne refais rien » — le prochain retour réseau, ou le bouton « Réessayer »,
    // rejoue le MÊME `client_attempt_id`, jamais un nouveau.
    if (outcome === "server_error") this.showFailureOverlay();
  }

  retry() {
    const pending = this.buffer();
    if (pending) this.send(pending);
  }

  // 🔴 **L'instant d'OUVERTURE est LU dans le formulaire, jamais fabriqué ici** —
  // `AR-1604-10`. Le champ caché est frappé par le SERVEUR au rendu de la page : le
  // chemin sans JS le poste tel quel, celui-ci le recopie dans sa charge JSON. **Une
  // seule déclaration, deux transports** — et c'est le même paramètre que le mobile
  // envoie déjà dans son enveloppe de synchronisation.
  //
  // ⚠️ **`new Date()` ici serait une seconde convention, et une PIRE.** L'horloge du
  // navigateur est celle de l'appareil : en avance, elle donnerait un début FUTUR, que
  // `Attempts::Recorder#bounded_start` ramène à l'instant de fin. La durée retomberait à
  // ZÉRO — le défaut d'origine, ressuscité sur les appareils que le produit vise.
  //
  // ⚠️ **Le tampon local n'a pas à le porter** : la clé de tampon contient le
  // `client_attempt_id`, frappé au même rendu que ce champ. Un rejeu — bouton
  // « Réessayer » ou retour du réseau — relit donc le formulaire qui a fabriqué
  // l'identifiant qu'il rejoue, jamais celui d'une autre séance.
  declaredStart() {
    return new FormData(this.formTarget).get("started_at");
  }

  setSending(sending) {
    if (!this.hasSubmitBtnTarget) return;
    this.submitBtnTarget.disabled = sending;
  }

  // ---- L'écran 17a -----------------------------------------------------

  // ⚠️ **Le compte de questions ne varie PAS d'un envoi à l'autre** — c'est le
  // même formulaire qui envoie TOUTES les réponses en un seul POST (aucune
  // sauvegarde question par question sur ce bloc, contrairement au player
  // partagé). `kept_title` est donc déjà pluralisé CÔTÉ SERVEUR, sur le nombre
  // réel de questions du bloc (voir la vue) — pas reconstruit ici sur un compte
  // qui pourrait mentir si le tampon perdait une entrée en route.
  // ⚠️ Markup en classes (`go4learn_chrome.css`), pas en style inline : le
  // budget de page (`T-07`) compte le JS ET le CSS, mais un attribut `style`
  // répété sur six blocs pèse en clair dans CE fichier, alors qu'une règle
  // partagée se compresse une fois pour toute la page.
  showFailureOverlay() {
    this.hideFailureOverlay();
    const overlay = document.createElement("div");
    overlay.className = "g4l-interstitial";
    overlay.dataset.testid = "quiz-save-failure";
    overlay.innerHTML = this.overlayMarkup();
    overlay.querySelector("[data-role=retry]")
      .addEventListener("click", () => this.retry());
    document.body.appendChild(overlay);
    this.overlay = overlay;
  }

  hideFailureOverlay() {
    this.overlay?.remove();
    this.overlay = null;
  }

  overlayMarkup() {
    const t = this.t;
    const meanwhile = this.hasFlashcardsUrlValue
      ? `<div class="g4l-interstitial__meanwhile"><strong>${t.meanwhile_title}</strong>
           <a href="${this.flashcardsUrlValue}">${t.flashcards_label}</a>
         </div>`
      : "";
    return `
      <div class="g4l-flash__item--danger g4l-interstitial__hero">
        <h2>${t.title}</h2><p>${t.subtitle}</p>
      </div>
      <div class="g4l-flash__item--succes g4l-interstitial__hero g4l-interstitial__kept">
        <span><b>${t.kept_title}</b><span>${t.kept_body}</span></span>
      </div>
      ${meanwhile}
      <span class="g4l-interstitial__ref">${t.ref.replace("%{code}", this.refCode())}</span>
      <div class="g4l-interstitial__actions">
        ${this.hasFlashcardsUrlValue ? `<a href="${this.flashcardsUrlValue}" class="g4l-learner-btn g4l-learner-btn--primary">${t.primary_cta}</a>` : ""}
        <button type="button" data-role="retry" class="g4l-learner-btn g4l-learner-btn--ghost">${t.retry_cta}</button>
      </div>`;
  }

  // 🔴 **Traçable jusqu'à la ligne serveur, jamais inventé** : `client_attempt_id`
  // est le MÊME identifiant que `Content::Attempt#client_attempt_id` — un support
  // qui reçoit cette référence retrouve la tentative exacte, pas un numéro
  // décoratif.
  refCode() {
    return `QZ-${this.formTarget.action.match(/attempt\/([0-9a-f-]{8})/)?.[1]?.toUpperCase() || "????????"}`;
  }

  headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
    };
  }

  buffer() {
    try {
      const raw = localStorage.getItem(this.storageKeyValue);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  saveBuffer(payload) {
    try {
      const body = payload.answers ? payload : { answers: payload, responses: {} };
      const entry = { ...body, action: this.formTarget.action, started_at: this.declaredStart() };
      localStorage.setItem(this.storageKeyValue, JSON.stringify(entry));
    } catch {
      // Quota saturé ou navigation privée : le tampon local est un confort de
      // rejeu, pas une garantie — l'envoi continue sans lui.
    }
  }

  clearBuffer() {
    try {
      localStorage.removeItem(this.storageKeyValue);
    } catch { /* voir saveBuffer */ }
  }
}
